import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { AlertCircle, CheckCircle2, KeyRound, ShieldCheck, ShieldOff, Copy } from 'lucide-react';
import QRCode from 'qrcode';
import { selectUser } from '../store/authSlice';
import { authApi } from '../api/client';

/**
 * Personal account settings — currently just change-password.
 *
 * Organisation-level settings stay on ConfigurationPage; this page is scoped
 * to the signed-in person, so nothing here takes an org UUID.
 */
export default function AccountPage() {
    const user = useSelector(selectUser);

    return (
        <div className="px-8 py-8 max-w-5xl">
            <div className="mb-6">
                <h2 className="font-display text-2xl font-semibold text-slate-900">
                    Account
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Signed in as {user?.email}
                </p>
            </div>

            <div className="space-y-6">
                <TwoFactorCard />
                <ChangePasswordCard />
            </div>
        </div>
    );
}

/**
 * Two-factor authentication.
 *
 * Three states, and the middle one is why enrolment is two steps: a secret is
 * issued so it can be scanned, but the factor is not switched on until the user
 * returns a code from it. Skipping that check would let a mis-scanned QR lock
 * the account at the next sign-in.
 */
function TwoFactorCard() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const [setup, setSetup] = useState(null);      // { secret, otpAuthUri }
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [code, setCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [disablePassword, setDisablePassword] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        authApi.mfaStatus()
            .then(setStatus)
            .catch(() => setError('Could not load your security settings.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => load(), [load]);

    // The QR is rendered in the browser from the otpauth URI, so the secret
    // never has to travel as an image and the backend needs no image library.
    useEffect(() => {
        if (!setup?.otpAuthUri) { setQrDataUrl(null); return; }
        QRCode.toDataURL(setup.otpAuthUri, { width: 200, margin: 1 })
            .then(setQrDataUrl)
            .catch(() => setQrDataUrl(null));
    }, [setup]);

    const begin = () => {
        setBusy(true); setError(null);
        authApi.mfaSetup()
            .then(setSetup)
            .catch((e) => setError(e.response?.data?.message || 'Could not start setup.'))
            .finally(() => setBusy(false));
    };

    const confirm = (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        authApi.mfaConfirm(code)
            .then((r) => { setRecoveryCodes(r.recoveryCodes); setSetup(null); setCode(''); load(); })
            .catch((err) => setError(err.response?.data?.message || 'That code was not accepted.'))
            .finally(() => setBusy(false));
    };

    const disable = (e) => {
        e.preventDefault();
        setBusy(true); setError(null);
        authApi.mfaDisable(disablePassword)
            .then(() => { setDisablePassword(''); setRecoveryCodes(null); load(); })
            .catch((err) => setError(err.response?.data?.message || 'Could not turn it off.'))
            .finally(() => setBusy(false));
    };

    const regenerate = () => {
        setBusy(true); setError(null);
        authApi.mfaRegenerateRecoveryCodes()
            .then((r) => { setRecoveryCodes(r.recoveryCodes); load(); })
            .catch((err) => setError(err.response?.data?.message || 'Could not create new codes.'))
            .finally(() => setBusy(false));
    };

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-md">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">
                Two-factor authentication
            </h3>
            <p className="text-sm text-slate-500 mb-4">
                Ask for a code from your phone as well as your password when signing in.
            </p>

            {error && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Shown once, and never retrievable again — only replaceable. */}
            {recoveryCodes && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-md">
                    <p className="text-sm font-medium text-amber-900 mb-1">
                        Save your recovery codes now
                    </p>
                    <p className="text-xs text-amber-800 mb-3">
                        Each code works once, and they are the only way back in if you lose
                        your phone. They will not be shown again.
                    </p>
                    <div className="grid grid-cols-2 gap-1 font-mono text-sm text-slate-800">
                        {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
                    </div>
                    <button
                        onClick={() => navigator.clipboard?.writeText(recoveryCodes.join('\n'))}
                        className="mt-3 flex items-center gap-1.5 text-xs text-amber-900 hover:text-amber-950"
                    >
                        <Copy size={13} /> Copy all
                    </button>
                </div>
            )}

            {loading ? (
                <p className="text-sm text-slate-400">Loading…</p>
            ) : setup ? (
                <form onSubmit={confirm} className="space-y-4">
                    <p className="text-sm text-slate-600">
                        Scan this with Google Authenticator, 1Password, or any
                        authenticator app, then enter the code it shows.
                    </p>
                    {qrDataUrl && (
                        <img src={qrDataUrl} alt="Authenticator QR code"
                             className="border border-slate-200 rounded-md" />
                    )}
                    <div>
                        <p className="text-xs text-slate-500 mb-1">
                            Can&apos;t scan? Enter this key by hand:
                        </p>
                        <code className="block text-xs font-mono break-all bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-slate-700">
                            {setup.secret}
                        </code>
                    </div>
                    <div>
                        <label htmlFor="mfa-code" className="block text-sm font-medium text-slate-700 mb-1">
                            Code from your app
                        </label>
                        <input
                            id="mfa-code" type="text" required autoComplete="one-time-code"
                            value={code} onChange={(e) => setCode(e.target.value)} disabled={busy}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button type="submit" disabled={busy}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300">
                            <ShieldCheck size={16} />
                            {busy ? 'Checking…' : 'Turn on'}
                        </button>
                        <button type="button" onClick={() => { setSetup(null); setCode(''); }}
                                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">
                            Cancel
                        </button>
                    </div>
                </form>
            ) : status?.enabled ? (
                <div className="space-y-4">
                    <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                        <ShieldCheck size={16} className="mt-0.5 flex-shrink-0" />
                        <span>
                            Switched on. {status.recoveryCodesRemaining} recovery{' '}
                            {status.recoveryCodesRemaining === 1 ? 'code' : 'codes'} left.
                        </span>
                    </div>

                    {status.recoveryCodesRemaining <= 3 && (
                        <p className="text-xs text-amber-700">
                            You are running low on recovery codes — create a new set.
                        </p>
                    )}

                    <button onClick={regenerate} disabled={busy}
                            className="text-sm text-brand-600 hover:text-brand-700">
                        Create new recovery codes
                    </button>

                    <form onSubmit={disable} className="pt-4 border-t border-slate-100 space-y-2">
                        <label htmlFor="disable-password" className="block text-sm font-medium text-slate-700">
                            Turn off two-factor authentication
                        </label>
                        {/* The password is required as well as the session: this is
                            the step that removes the protection. */}
                        <p className="text-xs text-slate-500">
                            Confirm your password to switch it off.
                        </p>
                        <input
                            id="disable-password" type="password" required autoComplete="current-password"
                            value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
                            disabled={busy}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                        <button type="submit" disabled={busy}
                                className="flex items-center gap-2 px-4 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50">
                            <ShieldOff size={15} /> Turn off
                        </button>
                    </form>
                </div>
            ) : (
                <button onClick={begin} disabled={busy}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300">
                    <ShieldCheck size={16} />
                    {busy ? 'Starting…' : 'Set up two-factor authentication'}
                </button>
            )}
        </div>
    );
}

function ChangePasswordCard() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSaved(false);

        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match.');
            return;
        }

        setSaving(true);
        try {
            await authApi.changePassword(currentPassword, newPassword);
            setSaved(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            if (err.response?.status === 400) {
                setError(err.response.data?.message || 'Current password is incorrect.');
            } else {
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-md">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">
                Change password
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
                {saved && (
                    <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                        <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                        <span>Password updated.</span>
                    </div>
                )}

                <div>
                    <label htmlFor="current-password" className="block text-sm font-medium text-slate-700 mb-1">
                        Current password
                    </label>
                    <input
                        id="current-password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={saving}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                </div>

                <div>
                    <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-1">
                        New password
                    </label>
                    <input
                        id="new-password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={saving}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
                </div>

                <div>
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-1">
                        Confirm new password
                    </label>
                    <input
                        id="confirm-password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength={8}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={saving}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                    <KeyRound size={16} />
                    {saving ? 'Updating...' : 'Update password'}
                </button>
            </form>
        </div>
    );
}
